import {
  Button as MuiButton,
  type ButtonProps as MuiButtonProps,
  IconButton as MuiIconButton,
  type IconButtonProps as MuiIconButtonProps
} from "@mui/material";

import React, { forwardRef } from "react";

const CICONBUTTON_A11Y_WARNED_KEYS = new Set<string>();

function getCIconButtonWarnKey(props: CIconButtonProps): string {
  // Best-effort callsite key to avoid spamming the console during re-renders.
  // Stack format varies by browser; we only need a stable-ish signature in dev.
  const stack = new Error().stack || "";
  const callsite = stack.split("\n")[3]?.trim() || stack.split("\n")[2]?.trim() || "";

  const childType = React.isValidElement(props.children)
    ? typeof props.children.type === "string"
      ? props.children.type
      : (props.children.type as any)?.displayName || (props.children.type as any)?.name || "Anonymous"
    : typeof props.children;

  return [callsite, props.component, props.size, props.color, props.edge, childType].filter(Boolean).join("|");
}

function pickCIconButtonDebugProps(props: CIconButtonProps) {
  return {
    "aria-label": props["aria-label"],
    "aria-labelledby": props["aria-labelledby"],
    title: (props as any).title,
    disabled: props.disabled,
    size: props.size,
    color: props.color,
    edge: props.edge,
    component: props.component,
    onClick: typeof props.onClick === "function"
  };
}

/**
 * CButtonProps - Wrapper interface around MUI Button
 *
 * This interface serves as the abstraction layer for all CButton usage.
 * It allows us to:
 * - Enforce consistent usage patterns across the codebase
 * - Customize styling/behavior independently from MUI updates
 * - Limit exposed MUI features to those needed by the project
 * - Add project-specific props or defaults in the future
 */
export interface CButtonProps extends MuiButtonProps {
  // Currently extends MUI fully, but this is where we can:
  // - Omit unused variants
  // - Add custom defaults
  // - Enforce specific size/color combinations
  // - Add domain-specific props
}

/**
 * CButton - Wrapper around MUI Button for project sustainability
 *
 * This wrapper provides an abstraction layer that isolates our codebase
 * from direct MUI Button dependency. If MUI Button changes or we need to
 * customize behavior, we only change this component, not all call sites.
 *
 * Uses forwardRef to allow parent components (like Tooltip) to access the DOM node.
 *
 * @example
 * ```tsx
 * <CButton variant="contained" onClick={handleClick}>
 *   Click me
 * </CButton>
 * ```
 */
export const CButton = forwardRef<HTMLButtonElement, CButtonProps>((props: CButtonProps, ref): React.ReactElement => {
  const { type = "button", ...rest } = props;

  return <MuiButton ref={ref} type={type} {...rest} />;
});

/**
 * CIconButtonProps - Wrapper interface around MUI IconButton
 *
 * Similar to CButtonProps, this abstraction layer allows future
 * customization and isolates library changes.
 */
export interface CIconButtonProps extends MuiIconButtonProps {
  // Currently extends MUI fully, but can be customized:
  // - Enforce icon-only usage
  // - Set sensible defaults for padding
  // - Limit to specific color variants
  // - Add custom props for the project
}

/**
 * CIconButton - Wrapper around MUI IconButton for project sustainability
 *
 * Use this for icon-only buttons. The wrapper provides an abstraction
 * that isolates our codebase from MUI IconButton changes.
 *
 * Uses forwardRef to allow parent components (like Tooltip) to access the DOM node.
 *
 * @example
 * ```tsx
 * <CIconButton onClick={handleClose} aria-label="close">
 *   <CloseIcon />
 * </CIconButton>
 * ```
 */
export const CIconButton = forwardRef<HTMLButtonElement, CIconButtonProps>(
  (props: CIconButtonProps, ref): React.ReactElement => {
    const { children, "aria-label": ariaLabel, sx, ...rest } = props;

    // Development warning for missing aria-label
    if (process.env.NODE_ENV === "development" && !ariaLabel && !props["aria-labelledby"]) {
      const warnKey = getCIconButtonWarnKey(props);
      if (!CICONBUTTON_A11Y_WARNED_KEYS.has(warnKey)) {
        CICONBUTTON_A11Y_WARNED_KEYS.add(warnKey);
        const hasTitle = Boolean((props as any).title);
        console.warn(
          `CIconButton: Missing aria-label or aria-labelledby. ${
            hasTitle ? "A title prop was provided, but it is not a reliable screen reader label." : ""
          }`,
          pickCIconButtonDebugProps(props)
        );
      }
    }

    // WCAG 2.2 Level AA touch target requirement: 44x44px minimum
    // Merge with any custom sx props, allowing overrides if needed
    const defaultSx = {
      minWidth: 44,
      minHeight: 44
    };

    const mergedSx = sx ? { ...defaultSx, ...sx } : defaultSx;

    return (
      <MuiIconButton ref={ref} aria-label={ariaLabel} sx={mergedSx} {...rest}>
        {React.isValidElement(children)
          ? React.cloneElement(children as React.ReactElement, {
              "aria-hidden": true
            })
          : children}
      </MuiIconButton>
    );
  }
);

export default CButton;
